import * as React from "react";

import { useFilterContext } from "./State/FilterContext";
import { useFilterDispatchContext } from "./State/FilterDispatchContext";
import { Fragment, useMemo } from "react";
import { isDefaultFilter } from "./GroopitFilterDTO";
import {
  createFilterRemoveAssignmentAction,
  createFilterRemoveChoiceAction,
  createFilterRemoveComponentAction,
  createFilterRemoveCreatorAction,
  createFilterRemoveDataTypeAction,
  createFilterRemoveDateRangeAction,
  createFilterRemoveKeywordQueryAction,
  createFilterRemoveTagAction,
} from "./State/FilterActions";
import { Chip } from "@material-ui/core";
import { useGroupContext } from "groopit-js";
import { ChipWithBlackText } from "../styles/MaterialGroopTheme";
import {
  getComponentFilterDescription,
  getDateDescription,
  ReadableDataTypeNames,
} from "./FilterStrings";

/// <summary>
/// Displays the filter in a chip-ified manner that's interactable.
/// </summary>
/// <remarks>
/// For a simpler text-only version see the FilterSummary component.
/// </remarks>
export const FilterDescription = () => {
  const { group } = useGroupContext();
  const { filter } = useFilterContext();
  const { filterDispatcher } = useFilterDispatchContext();

  const includedComponentsByAssignment = useMemo(() => {
    const includedComponentsByAssignment = new Map<string, string[]>();
    filter.filter.assignments.forEach((assignmentFilter) => {
      // If there aren't any component model ids then we aren't excluding anything
      if (!assignmentFilter.componentModelIds) {
        return;
      }
      const includedComponents: string[] = [];
      const assignment = group.assignments?.find(
        (a) => a.id === assignmentFilter.id,
      );
      // If we couldn't find the assignment then this is a stale filter and it really
      // won't show up in the description anyway.
      if (!assignment) {
        return;
      }

      for (const component of assignment.components) {
        if (
          assignmentFilter.componentModelIds.includes(
            component.componentModel_Id,
          )
        ) {
          includedComponents.push(component.description);
        }
      }

      // If we found every single component then we aren't actually excluding anything
      if (includedComponents.length === assignment.components.length) {
        return;
      }

      includedComponentsByAssignment.set(
        assignmentFilter.id,
        includedComponents,
      );
    });
    return includedComponentsByAssignment;
  }, [filter.filter.assignments, group.assignments]);

  if (!isDefaultFilter(filter)) {
    return (
      <div className="space-y-2">
        {filter.filter.assignments.map((assignment) => (
          <Fragment key={assignment.id}>
            <div className="flex flex-wrap gap-2">
              <ChipWithBlackText
                color="primary"
                variant="outlined"
                className="max-w-full"
                label={assignment.title}
                onDelete={() =>
                  filterDispatcher(
                    createFilterRemoveAssignmentAction(assignment.id),
                  )
                }
              />
              {assignment.creators?.map((creator) => (
                <Chip
                  key={creator.id}
                  color="primary"
                  className="max-w-full"
                  label={creator.friendlyName}
                  onDelete={() =>
                    filterDispatcher(
                      createFilterRemoveCreatorAction(
                        creator.id,
                        assignment.id,
                      ),
                    )
                  }
                />
              ))}
              {assignment.dateRange && (
                <Chip
                  color="primary"
                  className="max-w-full"
                  label={getDateDescription(assignment.dateRange)}
                  onDelete={() =>
                    filterDispatcher(
                      createFilterRemoveDateRangeAction(assignment.id),
                    )
                  }
                />
              )}
              {assignment.components?.map((component) => (
                <Chip
                  key={component.componentModelId}
                  color="primary"
                  className="max-w-full"
                  label={getComponentFilterDescription(component)}
                  onDelete={() =>
                    filterDispatcher(
                      createFilterRemoveComponentAction(
                        component.componentModelId,
                        assignment.id,
                      ),
                    )
                  }
                />
              ))}
            </div>
            {includedComponentsByAssignment.has(assignment.id) && (
              <>
                <div className="text-sm">AI insight will analyze:</div>
                <div className="flex flex-wrap gap-2">
                  {includedComponentsByAssignment
                    .get(assignment.id)
                    ?.map((component) => (
                      <Chip
                        key={component}
                        color="secondary"
                        className="max-w-full"
                        label={component}
                      />
                    ))}
                </div>
              </>
            )}
          </Fragment>
        ))}
        <div className="flex flex-wrap gap-2">
          {filter.filter.creators?.map((creator) => (
            <Chip
              key={creator.id}
              color="primary"
              className="max-w-full"
              label={creator.friendlyName}
              onDelete={() =>
                filterDispatcher(createFilterRemoveCreatorAction(creator.id))
              }
            />
          ))}
          {filter.filter.dateRange && (
            <Chip
              color="primary"
              className="max-w-full"
              label={getDateDescription(filter.filter.dateRange)}
              onDelete={() =>
                filterDispatcher(createFilterRemoveDateRangeAction())
              }
            />
          )}
          {filter.filter.tags?.map((tag) => (
            <Chip
              key={tag}
              color="primary"
              className="max-w-full"
              label={`#${tag}`}
              onDelete={() =>
                filterDispatcher(createFilterRemoveTagAction(tag))
              }
            />
          ))}
          {filter.filter.choices?.map((choice) => (
            <Chip
              key={choice}
              color="primary"
              className="max-w-full"
              label={`#${choice}`}
              onDelete={() =>
                filterDispatcher(createFilterRemoveChoiceAction(choice))
              }
            />
          ))}
          {filter.filter.feedFilterDataTypes?.map((dataType) => (
            <Chip
              key={dataType}
              color="primary"
              className="max-w-full"
              label={ReadableDataTypeNames.get(dataType)}
              onDelete={() =>
                filterDispatcher(createFilterRemoveDataTypeAction(dataType))
              }
            />
          ))}
          {filter.filter.query.map((keyword, i) => (
            <Chip
              key={i}
              color="primary"
              className="max-w-full"
              label={keyword}
              onDelete={() =>
                filterDispatcher(createFilterRemoveKeywordQueryAction(keyword))
              }
            />
          ))}
        </div>
      </div>
    );
  } else {
    return null;
  }
};
